/**
 * 아이마다 다른 **머리·어깨 자리**를 반영해 모자·소품을 놓는다.
 *
 * ## 왜 필요한가
 * `OVERLAY_FIT` 은 아이템당 값이 하나다(프레임 기준). 그런데 아이마다 머리 크기와 몸 비율이
 * 다르다 — 실측(512 프레임): 목 높이 152~197px(8.8%) · 머리 중심 241~261px(3.9%) ·
 * 머리 폭 181~206px(6.5%). 100px 로 그리면 목 높이만 9px 차이라, 가슴 배지가 어떤 아이는
 * 목에 어떤 아이는 배에 붙는다. **어느 아이에 맞추든 나머지 셋이 어긋나는** 구조라
 * 아이템 표를 더 만져서는 못 고친다.
 *
 * 그래서 층을 하나 더 둔다: 아이템 표는 "평균적인 아이" 기준으로 두고, 그 아이가 평균과
 * 얼마나 다른지만 여기서 보정한다. 아이템 표는 **손대지 않는다.**
 *
 * ## 배치 수식 (왜 이렇게 되는가)
 * 두 렌더러 모두 오버레이를 `프레임 중심 기준으로 scale 배 → (dx,dy) 만큼 이동` 시킨다.
 * 그림 안의 어느 점 `q`(중심 기준 비율)는 `s·q + d` 로 간다.
 *
 * 아이템의 기본값 `(s0,d0)` 은 **평균 아이에게 맞다** — 즉 그림의 기준점 `A` 가
 * `P = s0·A + d0` 에 놓였을 때 맞아 보인다는 뜻이고, 이 `P` 가 곧 평균 아이의 머리(또는 어깨)
 * 자리 `refAnchor` 다. 목표 아이에게서는 그 점이 `refAnchor + Δ` 로 가야 하고, 크기도 `k` 배여야 한다.
 *
 *   s·A + d = refAnchor + Δ,  s = k·s0
 *   → d = d0 + Δ + s0·A·(1−k) = **d0·k + refAnchor·(1−k) + Δ**
 *
 * 그림 속 `A` 가 어디인지 몰라도 된다는 게 요점이다 — `s0·A = refAnchor − d0` 로 소거된다.
 *
 * ## 기준점은 물건마다 다르다
 * 모자는 **머리**에, 가슴 소품·메달은 **어깨**에 붙는다. 같은 보정을 쓰면 모자가 어깨를
 * 따라 내려간다.
 */

export interface HeroAnchor {
  /** 머리(머리카락 포함) 폭·중심 x·세로 한가운데 — 프레임 한 변 대비 비율. */
  headW: number;
  headCx: number;
  headMidY: number;
  /** 턱 바로 아래(가슴) 중심 x·높이. */
  chestCx: number;
  chestY: number;
  /** 손(랜턴처럼 **쥐는** 물건의 기준점). 옆모습은 앞쪽 손, 앞·뒤는 보는 사람 기준 왼손. */
  handCx: number;
  handY: number;
}

/**
 * **손에 쥐는 물건**의 그림 속 손잡이 지점(그 그림 프레임 대비 비율).
 *
 * 랜턴은 몸에 붙이는 게 아니라 **손잡이를 손이 잡는다**. 가슴 기준으로 놓으면 배 옆에 뜬
 * 물건이 된다(실제로 그랬다). 여기 적힌 물건은 "평균과의 차이" 보정을 쓰지 않고
 * **손 자리에 직접 갖다 놓는다** — 손 위치는 아이·방향마다 크게 달라서(옆모습은 앞쪽 손을
 * 쓰므로 좌우가 통째로 바뀐다) 평균에서 밀어내는 방식이 성립하지 않는다.
 *
 * 값은 그림에서 잰다: 실루엣 맨 윗줄(손잡이 아치)의 가운데.
 */
export const HELD_GRIP: Readonly<Record<string, { x: number; y: number }>> = {
  // 손등 랜턴. 세로 픽셀 프로필로 잰 값 — 굵은 윗바(y262~274) 아래, **가는 기둥 구간**
  // (y276~296, 채워진 픽셀 26~45)의 가운데가 실제로 손가락이 감기는 자리다.
  // 꼭대기를 잡으면 손잡이가 손 위로 솟아 "들고 있다"로 안 읽힌다.
  "hero.acc.a3": { x: 0.715, y: 0.559 },
};

/** 이 오버레이가 몸의 어디에 붙는가. */
export type AnchorPart = "head" | "chest";

/**
 * 아이템 키 → 붙는 자리. 모자(`hero.hair.*`)만 머리다.
 * 모르는 키는 가슴 — 머리로 잘못 잡아 정수리 위로 날아가는 것보다 덜 튄다.
 */
export function anchorPartOf(key: string): AnchorPart {
  return key.startsWith("hero.hair.") ? "head" : "chest";
}

/**
 * 룩 키(`hero.look.b3_o1`)에서 **아이+옷**을 뽑는다(`b3_o1`).
 *
 * ⚠️ 아이만으로는 부족하다. **옷마다 인물이 프레임 안에서 다른 자리·다른 크기로 그려져 있다** —
 * b2 는 기본옷이 y6 에서 시작해 키 500, 우비는 y54 에서 시작해 키 452다. 기본옷 기준
 * 가슴 높이를 우비에 쓰면 그건 **어깨**가 된다(별 배지가 어깨에 붙었다, 2026-07-29).
 */
export function anchorLookOf(look: string): string {
  const m = /(?:^|[._/])(b\d+_o\d+)(?:\.|$)/.exec(look);
  if (m?.[1]) return m[1];
  const b = /(?:^|[._/])(b\d+)(?:_|$)/.exec(look);
  return b?.[1] ?? "";
}

/** 아이 id 만(`b3`) — 관리자 「이 아이만」 키 등에서 쓴다. */
export function anchorBodyOf(look: string): string {
  const m = /(?:^|[._/])(b\d+)(?:_|$)/.exec(look);
  return m?.[1] ?? "";
}

/**
 * 아이·옷이 어긋나 있어도 화면은 정상 — 모르면 평균값(=보정 없음).
 * 옷을 모르면 그 아이의 기본옷으로, 방향을 모르면 정면으로 떨어진다.
 */
export function heroAnchor(look: string, dir: string): HeroAnchor {
  const k = anchorLookOf(look);
  const b = anchorBodyOf(look);
  return (
    HERO_ANCHOR[`${k}.${dir}`] ??
    HERO_ANCHOR[`${k}.down`] ??
    HERO_ANCHOR[`${b}_o0.${dir}`] ??
    HERO_ANCHOR[`${b}_o0.down`] ??
    ANCHOR_REF
  );
}

/** 조절값 한 벌(오버레이 배치). heroOverlayFit 의 `OverlayFit` 과 같은 모양. */
interface FitLike {
  scale: number;
  dx: number;
  dy: number;
}

/**
 * 아이템 기본 배치 → **이 아이에게 맞춘 배치**. 위 수식 그대로다.
 *
 * 가슴 물건은 **옮기기만** 하고 크기는 안 바꾼다(k=1). 가슴 폭을 재 봤더니 0.105~0.203 으로
 * 두 배나 벌어졌는데, 그건 아이 차이가 아니라 **옆모습 가슴이 정면보다 좁은 자세** 차이였다.
 * 배율로 쓰면 배지가 옆을 볼 때마다 두 배로 커진다.
 *
 * 보정 폭은 아이 간 실제 차이(수 %)라 따로 안 묶는다 — 최종값은 렌더 직전에 한계로 잘린다.
 */
/**
 * **쥐는 물건** 배치 — 손잡이가 손에 오게 한다.
 *
 * 평균과의 차이로 밀어내는 `anchoredFit` 과 달리 **절대 위치**로 놓는다:
 * 그림 속 손잡이 지점 `g` 가 그 아이·그 방향의 손 자리에 정확히 가도록 이동량을 역산한다.
 *   s·(g − 0.5) + d = hand − 0.5   →   d = (hand − 0.5) − s·(g − 0.5)
 * 크기(`scale`)는 아이템 표가 정한 그대로 둔다 — 랜턴이 아이마다 커졌다 작아질 이유는 없다.
 */
export function grippedFit<T extends FitLike>(
  fit: T,
  look: string,
  dir: string,
  grip: { x: number; y: number },
): T {
  const a = heroAnchor(look, dir);
  return {
    ...fit,
    dx: a.handCx - 0.5 - fit.scale * (grip.x - 0.5),
    dy: a.handY - 0.5 - fit.scale * (grip.y - 0.5),
  };
}

export function anchoredFit<T extends FitLike>(fit: T, look: string, dir: string, part: AnchorPart): T {
  const a = heroAnchor(look, dir);
  const R = ANCHOR_REF;
  const head = part === "head";
  // 기준점(중심 기준 비율) — 프레임 중심이 0 이므로 0.5 를 뺀다
  const refX = (head ? R.headCx : R.chestCx) - 0.5;
  const refY = (head ? R.headMidY : R.chestY) - 0.5;
  const tgtX = (head ? a.headCx : a.chestCx) - 0.5;
  const tgtY = (head ? a.headMidY : a.chestY) - 0.5;
  // 모자는 그 아이 머리 폭만큼 키우고 줄인다. 예전엔 상한 1 로 묶어 뒀는데, 그건 모자 그림
  // 위에 여백이 없어 키우면 잘렸기 때문이다 — 2026-07-29 에 그림에 위 여백 12% 를 만들어
  // 그 제약이 사라졌다. 그래도 넘치면 `clampIntoFrame` 이 막는다.
  const k = head && R.headW > 0 ? a.headW / R.headW : 1;

  return {
    ...fit,
    scale: fit.scale * k,
    dx: fit.dx * k + refX * (1 - k) + (tgtX - refX),
    dy: fit.dy * k + refY * (1 - k) + (tgtY - refY),
  };
}

// <<<GENERATED — pnpm hero:anchors 가 여기부터 아래를 다시 쓴다. 손으로 고치지 말 것.
export const ANCHOR_REF: HeroAnchor = { headW: 0.3781, headCx: 0.49, headMidY: 0.1816, chestCx: 0.4884, chestY: 0.4038, handCx: 0.4412, handY: 0.6859 };

export const HERO_ANCHOR: Readonly<Record<string, HeroAnchor>> = {
  "b1_o0.down": { headW: 0.3809, headCx: 0.5, headMidY: 0.1738, chestCx: 0.5039, chestY: 0.3945, handCx: 0.3633, handY: 0.6953 },
  "b1_o0.side": { headW: 0.416, headCx: 0.5117, headMidY: 0.2139, chestCx: 0.4961, chestY: 0.4531, handCx: 0.6001, handY: 0.7422 },
  "b1_o0.up": { headW: 0.4023, headCx: 0.4814, headMidY: 0.1885, chestCx: 0.4775, chestY: 0.416, handCx: 0.3579, handY: 0.6948 },
  "b1_o1.down": { headW: 0.3359, headCx: 0.5107, headMidY: 0.1943, chestCx: 0.5098, chestY: 0.4355, handCx: 0.3379, handY: 0.6367 },
  "b1_o1.side": { headW: 0.3164, headCx: 0.4873, headMidY: 0.2148, chestCx: 0.4863, chestY: 0.459, handCx: 0.6728, handY: 0.6704 },
  "b1_o1.up": { headW: 0.3262, headCx: 0.4785, headMidY: 0.2012, chestCx: 0.4824, chestY: 0.4453, handCx: 0.333, handY: 0.7085 },
  "b1_o2.down": { headW: 0.4238, headCx: 0.498, headMidY: 0.1826, chestCx: 0.5, chestY: 0.4004, handCx: 0.3613, handY: 0.6953 },
  "b1_o2.side": { headW: 0.3809, headCx: 0.5098, headMidY: 0.1787, chestCx: 0.4814, chestY: 0.4004, handCx: 0.6695, handY: 0.6606 },
  "b1_o2.up": { headW: 0.4336, headCx: 0.4893, headMidY: 0.1787, chestCx: 0.4863, chestY: 0.4004, handCx: 0.3496, handY: 0.6997 },
  "b1_o3.down": { headW: 0.3626, headCx: 0.5, headMidY: 0.2129, chestCx: 0.5039, chestY: 0.423, handCx: 0.3633, handY: 0.7094 },
  "b1_o3.side": { headW: 0.4211, headCx: 0.5117, headMidY: 0.2083, chestCx: 0.4961, chestY: 0.4505, handCx: 0.6001, handY: 0.7431 },
  "b1_o3.up": { headW: 0.3418, headCx: 0.4941, headMidY: 0.1816, chestCx: 0.4697, chestY: 0.3926, handCx: 0.2588, handY: 0.6636 },
  "b2_o0.down": { headW: 0.4004, headCx: 0.5, headMidY: 0.1797, chestCx: 0.499, chestY: 0.4063, handCx: 0.3418, handY: 0.7012 },
  "b2_o0.side": { headW: 0.3633, headCx: 0.499, headMidY: 0.1807, chestCx: 0.4805, chestY: 0.4004, handCx: 0.6709, handY: 0.6563 },
  "b2_o0.up": { headW: 0.3496, headCx: 0.4863, headMidY: 0.1719, chestCx: 0.4844, chestY: 0.3867, handCx: 0.3281, handY: 0.6958 },
  "b2_o1.down": { headW: 0.3477, headCx: 0.499, headMidY: 0.2451, chestCx: 0.499, chestY: 0.4609, handCx: 0.3555, handY: 0.7188 },
  "b2_o1.side": { headW: 0.3398, headCx: 0.4775, headMidY: 0.2021, chestCx: 0.4863, chestY: 0.4434, handCx: 0.667, handY: 0.6783 },
  "b2_o1.up": { headW: 0.3652, headCx: 0.4863, headMidY: 0.1807, chestCx: 0.4854, chestY: 0.4043, handCx: 0.3282, handY: 0.7012 },
  "b2_o2.down": { headW: 0.4141, headCx: 0.499, headMidY: 0.2461, chestCx: 0.499, chestY: 0.4453, handCx: 0.3555, handY: 0.7246 },
  "b2_o2.side": { headW: 0.4082, headCx: 0.5156, headMidY: 0.1846, chestCx: 0.4805, chestY: 0.4082, handCx: 0.6802, handY: 0.664 },
  "b2_o2.up": { headW: 0.418, headCx: 0.4873, headMidY: 0.1836, chestCx: 0.4766, chestY: 0.4102, handCx: 0.2969, handY: 0.6919 },
  "b2_o3.down": { headW: 0.362, headCx: 0.5, headMidY: 0.2573, chestCx: 0.499, chestY: 0.4622, handCx: 0.3418, handY: 0.7288 },
  "b2_o3.side": { headW: 0.3711, headCx: 0.501, headMidY: 0.2041, chestCx: 0.4521, chestY: 0.4238, handCx: 0.645, handY: 0.686 },
  "b2_o3.up": { headW: 0.3482, headCx: 0.4863, headMidY: 0.1752, chestCx: 0.4844, chestY: 0.3891, handCx: 0.3281, handY: 0.697 },
  "b3_o0.down": { headW: 0.377, headCx: 0.5, headMidY: 0.1543, chestCx: 0.501, chestY: 0.3555, handCx: 0.3535, handY: 0.6563 },
  "b3_o0.side": { headW: 0.375, headCx: 0.4678, headMidY: 0.1855, chestCx: 0.4795, chestY: 0.4063, handCx: 0.6621, handY: 0.6485 },
  "b3_o0.up": { headW: 0.373, headCx: 0.4844, headMidY: 0.165, chestCx: 0.4766, chestY: 0.3691, handCx: 0.3135, handY: 0.6699 },
  "b3_o1.down": { headW: 0.374, headCx: 0.5, headMidY: 0.161, chestCx: 0.501, chestY: 0.3606, handCx: 0.3535, handY: 0.659 },
  "b3_o1.side": { headW: 0.3223, headCx: 0.4805, headMidY: 0.1689, chestCx: 0.4883, chestY: 0.3809, handCx: 0.6934, handY: 0.606 },
  "b3_o1.up": { headW: 0.3262, headCx: 0.4941, headMidY: 0.1885, chestCx: 0.4805, chestY: 0.4063, handCx: 0.3184, handY: 0.669 },
  "b3_o2.down": { headW: 0.4023, headCx: 0.499, headMidY: 0.1543, chestCx: 0.4971, chestY: 0.3516, handCx: 0.3242, handY: 0.6895 },
  "b3_o2.side": { headW: 0.3633, headCx: 0.5186, headMidY: 0.1641, chestCx: 0.4912, chestY: 0.3711, handCx: 0.6641, handY: 0.6519 },
  "b3_o2.up": { headW: 0.377, headCx: 0.4844, headMidY: 0.1768, chestCx: 0.4854, chestY: 0.3965, handCx: 0.3159, handY: 0.6636 },
  "b3_o3.down": { headW: 0.3027, headCx: 0.4961, headMidY: 0.1699, chestCx: 0.499, chestY: 0.3535, handCx: 0.3281, handY: 0.6055 },
  "b3_o3.side": { headW: 0.3105, headCx: 0.4863, headMidY: 0.1719, chestCx: 0.457, chestY: 0.3828, handCx: 0.6709, handY: 0.6401 },
  "b3_o3.up": { headW: 0.3715, headCx: 0.4844, headMidY: 0.1683, chestCx: 0.4766, chestY: 0.3716, handCx: 0.3135, handY: 0.6712 },
  "b4_o0.down": { headW: 0.375, headCx: 0.499, headMidY: 0.1748, chestCx: 0.5068, chestY: 0.3965, handCx: 0.3438, handY: 0.6973 },
  "b4_o0.side": { headW: 0.3691, headCx: 0.4766, headMidY: 0.2031, chestCx: 0.4844, chestY: 0.4414, handCx: 0.6489, handY: 0.6719 },
  "b4_o0.up": { headW: 0.3555, headCx: 0.4736, headMidY: 0.1885, chestCx: 0.4707, chestY: 0.4199, handCx: 0.3106, handY: 0.7007 },
  "b4_o1.down": { headW: 0.3398, headCx: 0.499, headMidY: 0.2217, chestCx: 0.5049, chestY: 0.4297, handCx: 0.3516, handY: 0.7148 },
  "b4_o1.side": { headW: 0.3184, headCx: 0.4766, headMidY: 0.1865, chestCx: 0.4785, chestY: 0.416, handCx: 0.7002, handY: 0.6529 },
  "b4_o1.up": { headW: 0.3379, headCx: 0.4785, headMidY: 0.1943, chestCx: 0.4795, chestY: 0.4277, handCx: 0.3028, handY: 0.6948 },
  "b4_o2.down": { headW: 0.4121, headCx: 0.5, headMidY: 0.2129, chestCx: 0.498, chestY: 0.418, handCx: 0.3477, handY: 0.7129 },
  "b4_o2.side": { headW: 0.3887, headCx: 0.5176, headMidY: 0.1865, chestCx: 0.4912, chestY: 0.4121, handCx: 0.6577, handY: 0.6508 },
  "b4_o2.up": { headW: 0.4199, headCx: 0.4941, headMidY: 0.1748, chestCx: 0.4854, chestY: 0.3926, handCx: 0.3013, handY: 0.6997 },
  "b4_o3.down": { headW: 0.3125, headCx: 0.4912, headMidY: 0.2227, chestCx: 0.499, chestY: 0.4141, handCx: 0.3281, handY: 0.6641 },
  "b4_o3.side": { headW: 0.3789, headCx: 0.4814, headMidY: 0.208, chestCx: 0.4521, chestY: 0.4473, handCx: 0.6836, handY: 0.6631 },
  "b4_o3.up": { headW: 0.3541, headCx: 0.4736, headMidY: 0.1917, chestCx: 0.4707, chestY: 0.4222, handCx: 0.3106, handY: 0.7018 },
};

/** 오버레이 그림이 자기 프레임 안에서 시작하는 높이(비율). 잘림 방지에 쓴다. */
export const OVERLAY_ART_TOP: Readonly<Record<string, number>> = {
  "hero.acc.a1": 0.4902,
  "hero.acc.a2": 0.416,
  "hero.acc.a3": 0.5098,
  "hero.acc.a4": 0.3711,
  "hero.hair.c1": 0.123,
  "hero.hair.c2": 0.127,
  "hero.hair.c3": 0.1387,
  "hero.hair.c4": 0.1348,
};
